import '_shared_/js/utils/consoleWrapper.js';
import '_shared_/js/utils/polyfills-new.js';
import Network from '_shared_/js/utils/Network';
import Vue from 'vue';
import * as Sentry from '@sentry/vue';
import { configureHostCommunication, initializeHostCommunication } from './communication';

import '../sass/app.scss';

Sentry.init({
    Vue,
    dsn:
        import.meta.env.VITE_ENV !== 'local'
            ? 'https://0f570b4c375b3264d546c61da4aafaa1@o460760.ingest.us.sentry.io/4508103611842560'
            : '',
    environment: import.meta.env.VITE_ENV,
    integrations: [],
    ignoreErrors: [
        // Readium's FXLFramePoolManager rejects offscreen iframe pre-load promises
        // with "Offscreen load timeout: ..." after a 15s watchdog (VOLPE-19H). The
        // rejection has no functional impact — the page either loads eventually or
        // gets disposed by the pool — but surfaces as an UnhandledRejection in
        // Sentry. Filtered here instead of patched in EpubViewer because the
        // upstream watchdog is not cancellable.
        /Offscreen load timeout:/,
        // The reader loads inside an iframe the reader can leave whenever they
        // want: closing the tab or navigating away cancels the requests in
        // flight, and the host surfaces those as an aborted request. The
        // cancellation is the reader's own doing, so there is nothing to act
        // on — unlike a request that reaches the network and fails, which
        // still reports.
        /Request aborted/,
    ],
});

// Set tags that don't depend on Delfino as early as possible
Sentry.setTags({
    host: import.meta.env.VITE_VOLPE_HOST,
});
/**
 * Disable tooltip for mobile
 * We import and use this right here, because other components, like ReaderMain, use v-tooltip.
 * Tooltip configuration for WCAG 1.4.13 compliance (hover + focus, dismissible, hoverable)
 */
import VTooltip from 'v-tooltip';

Vue.use(VTooltip, {
    defaultTrigger: 'hover focus',
    autoHide: false,
    disposeTimeout: 0,
});
VTooltip.enabled = window.innerWidth > 570;

// ESC key dismisses tooltips (WCAG 1.4.13)
document.addEventListener('keydown', event => {
    if (event.key === 'Escape') {
        const tooltips = document.querySelectorAll('.tooltip[aria-hidden="false"]');
        if (tooltips.length > 0) {
            document.querySelectorAll('[data-v-tooltip]').forEach(el => {
                if (el._tooltip && typeof el._tooltip.hide === 'function') {
                    el._tooltip.hide();
                }
            });
            tooltips.forEach(tooltip => {
                tooltip.setAttribute('aria-hidden', 'true');
            });
        }
    }
});
import Vuex from 'vuex';
import storeBase from './store';
import axios from 'axios';
import ReaderMain from './components/Reader.vue';
import SvgIcon from '_shared_/UI/Utilities/SvgIcon.vue';
import LoadingProcess from './LoadingProcess';
import VueLazyload from 'vue-lazyload';
import PortalVue from 'portal-vue';

// Global layout and component registration
import Container from '../../_shared_/UI/Layout/Container.vue';
import Row from '../../_shared_/UI/Layout/Row.vue';
import LayoutCol from '../../_shared_/UI/Layout/Col.vue';

Vue.component('Container', Container);
Vue.component('Row', Row);
Vue.component('LayoutCol', LayoutCol);

function freeze(reactiveObject) {
    return JSON.parse(JSON.stringify(reactiveObject));
}

// We a global Axios instance so we can set the default header in the LoadingProcess
window.axios = axios;

window.Vue = Vue;

const eventBus = new Vue();
// Enable access to the eventBus in all Vue components
Object.defineProperties(window.Vue.prototype, {
    $freeze: {
        get() {
            return freeze;
        },
    },
    $bus: {
        get() {
            return eventBus;
        },
    },
});

Vue.config.productionTip = import.meta.env.VITE_ENV !== 'local';

Vue.use(Vuex);
Vue.use(PortalVue);
Vue.use(VueLazyload, {
    // set observer to true
    observer: true,
    // optional
    observerOptions: {
        rootMargin: '0px',
        threshold: 0.1,
    },
    // Provide explicit loading and error images to prevent require() calls
    loading: 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7', // 1x1 transparent gif
    error: 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7', // 1x1 transparent gif
    attempt: 1,
});
Vue.directive('click-outside', {
    bind(el, binding, vnode) {
        el.clickOutsideEvent = function (event) {
            // here I check that click was outside the el and its children
            if (!(el == event.target || el.contains(event.target))) {
                // and if it did, call method provided in attribute value
                vnode.context[binding.expression](event);
            }
        };
        document.body.addEventListener('click', el.clickOutsideEvent);
    },
    unbind(el) {
        document.body.removeEventListener('click', el.clickOutsideEvent);
    },
});
Vue.component('SvgIcon', SvgIcon);
const store = new Vuex.Store(storeBase);

// Set initial document language for accessibility
document.documentElement.lang = store.state.lang;

configureHostCommunication(eventBus, store);

initializeHostCommunication()
    .then(() => {
        new Vue({
            ...ReaderMain,
            ...{
                store,
            },
        });
        const loadingProcess = new LoadingProcess(eventBus, store);
        loadingProcess.init();
    })
    .catch(error => console.error(error));

// Module evaluation: iOS WebKit can run this before `document.body` exists, and
// the event bubbles to the document anyway.
document.addEventListener('contextmenu', e => {
    e.preventDefault();
    e.stopPropagation();
});

Network.startCountNetworkStatus();
